export const projectsData = [
  {
    id: 1,
    title: 'Impression — AI-Driven Dating Profile Optimization',
    category: 'software, business',
    description: 'Founder & Lead Developer of an AI-driven platform for optimizing dating profiles using real-time A/B testing and GPT API integration.',
    longDescription: `# Impression — AI-Driven Dating Profile Optimization (Jul 2025 - Sept 2025)
**Role:** Founder & Lead Developer

**Overview**  
AI platform that optimizes dating profiles via real-time testing and feedback. Serves users seeking data-driven profile improvements to increase match rates and dating success.

**Problem / Challenge**  
Users lacked actionable insight to improve match rates; existing tools were subjective, slow, and non-repeatable. Dating app users struggled with profile optimization without measurable feedback on what changes actually improved outcomes.

**Approach / Solution**  
- Designed gamified rating loop and token incentives to increase user engagement
- Implemented A/B test framework for systematic profile variant testing
- Built real-time analytics to shorten feedback cycles from days to hours
- Developed GPT API integration for automated profile suggestions and optimization

**Impact / Results**  
- 200+ beta users across two pilot cohorts
- Feature adoption +50% post-token incentives implementation
- Shut down when distribution stalled

**Leadership & Collaboration**  
- Conducted weekly user interviews to align roadmap with pilot findings
- Resolved technical architecture conflicts through rapid prototyping and user testing

**Skills Applied**  
A/B testing, growth strategy, user research, funnel analysis, pricing experiments, product management, TypeScript, React, Firebase

**Selected Artifacts**  
[Live Demo](https://impressiondating.com) · [Code](https://github.com/Jaiparmar940/SwipeFix)

`,
    technologies: ['TypeScript', 'React', 'Firebase', 'GPT API', 'Cloud Functions'],
    image: `${process.env.PUBLIC_URL}/static/projects/impression/demo2.gif`,
    github: 'https://github.com/Jaiparmar940/SwipeFix',
    live: 'https://impressiondating.com',
    featured: true,
    hasDetailPage: true,
    detailSlug: 'impression-detail',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/impression/demo1.gif`,
      `${process.env.PUBLIC_URL}/static/projects/impression/demo2.gif`,
      `${process.env.PUBLIC_URL}/static/projects/impression/demo3.gif`,
      `${process.env.PUBLIC_URL}/static/projects/impression/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/impression/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/impression/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/impression/image4.jpeg`
    ]
  },
  {
    id: 2,
    title: 'Workly — Mobile Job Matching App',
    category: 'software, business',
    description: 'Full-featured SwiftUI and React Native application with real-time messaging, Firebase integration, and admin pipeline for job posting management.',
    longDescription: `# Workly — Mobile Job Matching App (2024 - Present)
**Role:** Full-Stack Developer & Product Manager

**Overview**  
Cross-platform mobile application connecting job seekers with employers through intelligent matching algorithms and real-time communication. Serves both iOS and Android users seeking streamlined job search and hiring processes.

**Problem / Challenge**  
Job seekers struggled with fragmented application processes and poor communication with employers. Employers faced inefficient candidate screening and lengthy hiring cycles without integrated communication tools.

**Approach / Solution**  
- Developed dual-platform architecture using SwiftUI and React Native for maximum reach
- Implemented real-time messaging system enabling direct employer-candidate communication
- Built intelligent matching algorithm based on skills, experience, and preferences
- Created comprehensive admin pipeline for job posting management and analytics

**Impact / Results**  
- Built a cross-platform mobile app in React Native with Expo, using Expo Router tab-based navigation and a fully TypeScript-typed codebase.
- Backed the app with Firebase Firestore, modeling users, jobs, applications, and AI-generated job matches as dedicated collections.
- Implemented an AI-powered matching algorithm that scores job-candidate fit by skills alignment, location/remote compatibility, experience and rating, and interest overlap, returning percentage match scores with reasoning.
- Developed a comprehensive job-posting flow supporting 30+ categories, multiple job types (one-time, part-time, full-time, contract, internship), complexity levels, on-site/remote options, and budget ranges.
- Added end-to-end application tracking with status states (Pending, Accepted, Rejected, Withdrawn), rate proposals, and job history, plus theme-aware UI components with dark/light mode support.
- Project was archived due to marketplace-related onboarding issues.

**Leadership & Collaboration**  
- Led cross-platform development team coordinating iOS and Android development
- Collaborated with backend team for Firebase integration and real-time features
- Resolved platform-specific challenges through unified codebase architecture
- Implemented agile development process with weekly sprint reviews

**Skills Applied**  
Mobile development, product management, real-time systems, user experience design, cross-platform architecture, SwiftUI, React Native, Firebase

**Selected Artifacts**  
[Code Repository](https://github.com/Jaiparmar940/workly.git)

`,
    technologies: ['SwiftUI', 'React Native', 'Firebase', 'Python', 'Firestore'],
    image: `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-1.webp`,
    github: 'https://github.com/Jaiparmar940/workly.git',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'workly-detail',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-1.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-2.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-3.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-4.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview-5.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/preview.webp`,
      `${process.env.PUBLIC_URL}/static/projects/workly-detail/screenshot17841830408188.png`
    ]
  },
  {
    id: 3,
    title: 'Full-Stack Banking Application',
    category: 'software, finance',
    description: 'Secure RESTful banking application with Spring Boot backend, React frontend, and comprehensive transaction management system.',
    longDescription: `# Full-Stack Banking Application (2023 - 2024)
**Role:** Full-Stack Developer & Security Architect

**Overview**  
Enterprise-grade banking application providing secure financial transaction management through modern web technologies. Serves financial institutions requiring robust, scalable, and secure transaction processing capabilities.

**Problem / Challenge**  
Traditional banking systems lacked modern user interfaces and real-time transaction capabilities. Security vulnerabilities in existing systems created risks for financial data and user transactions.

**Approach / Solution**  
- Architected secure RESTful API using Spring Boot with comprehensive authentication
- Implemented JWT-based authentication and Spring Security for data protection
- Developed responsive React frontend with real-time transaction updates
- Built comprehensive transaction management system with audit trails

**Impact / Results**  
- Learning-focused full-stack project (not production-deployed) co-developed with [Michael Levin](https://github.com/MichaelLevin5908), a YC founder and former GitHub developer
- Built end-to-end account lifecycle: registration, JWT login, balance/name enquiry, credit/debit, and peer-to-peer transfers with persisted transaction history
- Implemented Spring Security + JWT authentication, BCrypt password hashing, and React protected routes for authenticated dashboard access
- Added transactional email alerts (account creation, login, debit/credit) and PDF bank statement generation via iText
- Documented REST APIs with Swagger/OpenAPI and validated full local stack integration (Spring Boot, MySQL, React)

**Leadership & Collaboration**  
- Resolved complex data synchronization challenges between frontend and backend
- Implemented code review processes ensuring security best practices

**Skills Applied**  
Full-stack development, security architecture, API design, financial systems, authentication, Spring Boot, React, JWT

**Selected Artifacts**  
[Code Repository](https://github.com/Jaiparmar940/banking_application)

`,
    technologies: ['Spring Boot', 'React', 'Spring Security', 'JWT', 'REST APIs'],
    image: 'https://picsum.photos/400/250?random=3',
    github: 'https://github.com/Jaiparmar940/banking_application',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'banking-application-detail',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/banking-application-detail/preview.webp`,
      `${process.env.PUBLIC_URL}/static/projects/banking-application-detail/preview-1.webp`,
      `${process.env.PUBLIC_URL}/static/projects/banking-application-detail/preview-2.webp`,
      `${process.env.PUBLIC_URL}/static/projects/banking-application-detail/preview-3.webp`,
      `${process.env.PUBLIC_URL}/static/projects/banking-application-detail/preview-4.webp`
    ]
  },
  {
    id: 4,
    title: 'ANN Trade Programming',
    category: 'finance, software',
    description: 'Independent research project developing artificial neural networks for U.S. options price prediction using historical and sentiment data.',
    longDescription: `# ANN Trade Programming (2023 - 2024)
**Role:** Quantitative Research Analyst & ML Engineer

**Overview**  
Independent research project developing artificial neural networks for U.S. options price prediction using historical market data and sentiment analysis. Serves quantitative traders seeking data-driven options pricing models.

**Problem / Challenge**  
Traditional options pricing models (Black-Scholes) failed to capture market sentiment and behavioral factors. Traders needed more accurate pricing models incorporating both quantitative and qualitative market signals.

**Approach / Solution**  
- Developed deep learning models using TensorFlow for options price prediction
- Integrated sentiment analysis from news and social media data sources
- Implemented SARIMA time series analysis for market trend identification
- Built comprehensive data pipeline processing historical options and market data

**Impact / Results**  
- Combined 1-hour stock trend accuracy of 52.14% across 280 prediction intervals (146 correct / 134 incorrect); not statistically significant vs. chance (p = 0.23)
- Statistically significant uptrend accuracy of 68.26% (114 / 166); downtrend accuracy only 28.32%
- Index trend accuracy of 69.64% (39 / 56), with uptrend accuracy of 78.72% (37 / 47)
- Simulated trading returned +2.26% marginal profit over 14 market hours (66 trades; 40 profitable; p = 0.042)
- Buy signals were profitable 60.61% of the time; processed 100,000+ price data points into 336 forecast intervals

**Leadership & Collaboration**  
- Led independent research project from data collection to model deployment
- Collaborated with financial data providers for real-time market data access
- Resolved model overfitting challenges through regularization techniques
- Implemented rigorous backtesting framework for model validation

**Skills Applied**  
Quantitative analysis, machine learning, financial modeling, data science, sentiment analysis, Python, TensorFlow, Pandas, SARIMAx, Statsmodels

**Selected Artifacts**  
[Code Repository](https://github.com/Jaiparmar940/ann-trading)

`,
    technologies: ['Python', 'TensorFlow', 'Pandas', 'Sentiment Analysis', 'SARIMA'],
    image: `${process.env.PUBLIC_URL}/static/projects/ann-trade/image1.png`,
    github: 'https://github.com/Jaiparmar940/ann-trading',
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'ann-trade-detail',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/ann-trade/image1.png`,
      `${process.env.PUBLIC_URL}/static/projects/ann-trade/image2.png`,
      `${process.env.PUBLIC_URL}/static/projects/ann-trade/image3.png`,
      `${process.env.PUBLIC_URL}/static/projects/ann-trade/image4.png`,
      `${process.env.PUBLIC_URL}/static/projects/ann-trade/image5.png`
    ]
  },
  {
    id: 5,
    title: 'JPcommerce — Medical Equipment Resale Business',
    category: 'business, engineering',
    description: 'Bootstrapped business: sourced broken medical equipment — sterilizers, digital x-ray machines — repaired it myself, and sold it to private practices and underfunded health systems internationally. $100,000+ revenue over five years.',
    longDescription: `# JPcommerce — Medical Equipment Resale Business (Mar 2020 - May 2025)
**Role:** Founder

[Visit my eBay storefront](https://www.ebay.com/usr/jpcommerce)

**Overview**
Repair and resale business for medical equipment serving clinics and buyers in fragmented markets. Enabled underfunded healthcare facilities to access quality equipment at 30-50% below retail prices by sourcing broken units and repairing them myself.

**Problem / Challenge**  
Pricing opacity and inconsistent quality created margin risk and long repair cycles. Healthcare facilities in developing markets faced equipment shortages while developed markets had underutilized assets with no efficient redistribution mechanism.

**Approach / Solution**  
- Sourced over 100 dated and defective medical devices from online and local sources (used or for parts)
- Purchased devices and personally repaired/restored them by hand
- Ordered all necessary parts or sourced through other assemblies
- Packed or crated for freight shipping
- Listed, advertised, and sold units through an eBay storefront

**Impact / Results**  
- $100K+ revenue; 42% average profit margin across 100+ transactions
- 200+ five-star client reviews; 100% satisfaction rate maintained
- ROI improved +12% through freight shipping contracts
- Scaled from local to international markets within 18 months

**Skills Applied**  
Market analysis, pricing strategy, financial modeling, risk assessment, Excel

**Selected Artifacts**  
[Visit my eBay storefront](https://www.ebay.com/usr/jpcommerce)

`,
    technologies: ['Market Analysis', 'Equipment Restoration', 'E-commerce', 'Negotiation', 'Quality Assurance', 'International Logistics', 'Financial Modeling', 'Process Optimization', 'Risk Assessment'],
    image: 'https://picsum.photos/400/250?random=5',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'jpcommerce-detail',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image13.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image14.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image15.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image16.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image17.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image18.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image19.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image20.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image21.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image22.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image23.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image24.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image25.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image26.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image27.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/jpcommerce/image28.jpeg`
    ]
  },
  {
    id: 6,
    title: 'Structural Design and Analysis of a Pump–Support Platform',
    category: 'engineering',
    description: 'Engineered a height-adjustable platform to support a centrifugal pump via dual angled arms. Performed static equilibrium to determine reaction forces and moments, then carried out comprehensive stress analyses.',
    longDescription: `# Structural Design and Analysis of a Pump–Support Platform (2022 - 2023)
**Role:** Mechanical Design Engineer & Structural Analyst

**Overview**  
Height-adjustable structural platform designed to support centrifugal pumps in industrial applications. Serves manufacturing facilities requiring flexible pump positioning with precise load distribution and safety compliance.

**Problem / Challenge**  
Fixed-height pump installations limited operational flexibility and maintenance access. Existing support systems lacked proper load distribution, creating safety risks and structural failures in industrial environments.

**Approach / Solution**  
- Designed dual angled arm support system for optimal load distribution
- Performed comprehensive static equilibrium analysis to determine reaction forces
- Conducted detailed stress analysis ensuring structural integrity under load
- Implemented height adjustment mechanism for operational flexibility

**Impact / Results**  
- Withstood a 115 lbf operating load without visible bending, buckling, or failure.
- Achieved just 0.508 mm of measured deflection, beating the 1 mm requirement by 49%.
- Predicted physical deflection within ~1% using analytical beam calculations.
- Reduced platform weight by 30% through a custom I-beam redesign while maintaining nearly unchanged stiffness.
- Delivered a 3.8 lb, 19-inch prototype for $142.53, meeting all load, size, weight, and budget requirements.

**Leadership & Collaboration**  
- Led structural analysis team through design and validation phases
- Collaborated with manufacturing team for fabrication feasibility
- Resolved load distribution challenges through iterative design optimization
- Implemented safety protocols ensuring compliance with industrial standards

**Skills Applied**  
Structural analysis, mechanical design, CAD modeling, stress analysis, static equilibrium, industrial engineering, SolidWorks

`,
    technologies: ['Static Equilibrium Analysis', 'Stress Analysis', 'Structural Design', 'Mechanical Engineering', 'CAD Modeling'],
    image: 'https://picsum.photos/400/250?random=6',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'pump-support-platform',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/pump-support-platform/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/pump-support-platform/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/pump-support-platform/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/pump-support-platform/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/pump-support-platform/image5.jpeg`
    ]
  },
  {
    id: 7,
    title: 'Autonomous Block-Stacking Robot',
    category: 'engineering, software',
    description: 'Designed and built a robot that autonomously collects and stacks blocks to build towers. The robot was fully modelled in CAD and programmed using Java, OpenCV, TensorFlow, and Vuforia for computer vision and machine learning capabilities.',
    longDescription: `# Autonomous Block-Stacking Robot (2021 - 2022)
**Role:** Robotics Engineer & AI Developer

**Overview**  
Autonomous robotic system designed to collect and stack blocks to build towers using computer vision and machine learning. Serves manufacturing and logistics applications requiring automated object manipulation and stacking operations.

**Problem / Challenge**  
Manual block stacking operations were time-consuming and error-prone in manufacturing environments. Existing robotic systems lacked the intelligence to adapt to varying block positions and stacking patterns.

**Approach / Solution**  
- Competed in FTC competition as a one man team, managing design, fabrication, programming, documentation, and outreach alone
- Developed computer vision system using OpenCV for real-time block detection and tracking
- Implemented machine learning algorithms using TensorFlow for pattern recognition
- Built autonomous navigation system using Vuforia for spatial awareness
- Modelled entire robot in SolidWorks prior to construction
- Designed comprehensive CAD model for mechanical system optimization

**Impact / Results**  
- Founded a one-person FTC robotics team and independently led the robot's CAD, mechanical design, fabrication, assembly, and software development.
- Took the robot from concept to competition in approximately three months despite late registration, limited funding, and no initial workshop access.
- Fabricated custom aluminum components using waterjet cutting and manual machining, avoiding costly commercial CNC services.
- Rapidly redesigned the intake around unavailable parts and repaired mechanical failures during competition.
- Finished 7th at the team's first FTC meet, including a match win immediately after an in-event lift repair.
- Won Rockwell Collins Design award for most innovative robotic design at qualifier meet, qualified for state tournament but cancelled due to COVID

**Leadership & Collaboration**  
- Following state championship qualifications, outreached and established partnership with Dunn School's robotics team who was eliminated
- Integrated teams together and delegated tasks for complete robot redesign in two weeks
- Outreached to lower grades in the school to teach them about robotics and encourage students to eventually join the program

**Skills Applied**  
Robotics engineering, computer vision, machine learning, autonomous systems, CAD modeling, Java, OpenCV, TensorFlow, Vuforia

`,
    technologies: ['Java', 'OpenCV', 'TensorFlow', 'Vuforia', 'CAD Modeling', 'Robotics', 'Computer Vision', 'Machine Learning', 'Autonomous Systems'],
    image: 'https://picsum.photos/400/250?random=7',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'autonomous-robot',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image13.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image14.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image15.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image16.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image18.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image19.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image20.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image21.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image22.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image23.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image24.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image25.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image26.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image27.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image28.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image29.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image30.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image31.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/autonomous-robot/image32.jpeg`
    ]
  },
  {
    id: 8,
    title: 'Design & Testing of a Mousetrap-Powered Sand Vehicle',
    category: 'engineering',
    description: 'Developed and iteratively refined a tricycle-style sand vehicle powered by a single mousetrap. Explored sled vs. wheel drives, gear ratios, and sand-traction issues through hands-on prototyping.',
    longDescription: `# Design & Testing of a Mousetrap-Powered Sand Vehicle (2021 - 2022)
**Role:** Mechanical Design Engineer & Prototype Developer

**Overview**  
Innovative sand vehicle design powered by a single mousetrap spring mechanism, optimized for maximum distance and efficiency on sandy terrain. Serves as a proof-of-concept for alternative propulsion systems in challenging environments.

**Problem / Challenge**  
Traditional propulsion systems were ineffective on loose sand surfaces. Existing vehicles lacked the power-to-weight ratio and traction necessary for efficient sand navigation, requiring innovative mechanical solutions.

**Approach / Solution**  
- Developed tricycle-style chassis design optimized for sand traction and stability
- Conducted comparative analysis of sled vs. wheel drive systems for sand performance
- Implemented variable gear ratio system for optimal power transfer
- Built iterative prototyping process for rapid design optimization

**Impact / Results**  
- Designed and fabricated a single-mousetrap-powered sand vehicle that traveled 40 inches while weighing only 0.939 lb.
- Delivered one of the top-performing designs in the class evaluation.
- Improved range through iterative testing of wheel geometry, traction, drivetrain orientation, sled configurations, and pulley mechanisms.
- Eliminated drivetrain stalling by angling the mousetrap and converting the vehicle to a rear-wheel-drive tricycle layout.
- Increased testing throughput by organizing a parallel reset process for string winding, sand preparation, and vehicle weighing.

**Leadership & Collaboration**  
- Helped establish a fast, consensus-driven design process that kept the team aligned while evaluating multiple competing concepts.
- Organized testing around parallel roles for string setup, sand preparation, and vehicle weighing, increasing trial throughput and accelerating iteration.
- Encouraged the team to validate ideas experimentally rather than eliminate concepts prematurely, leading to evidence-based design decisions.
- Collaborated across mechanical design, prototyping, and testing to diagnose friction, traction, and stalling issues and convert observations into concrete design changes.
- Contributed to a team culture of rapid iteration, shared ownership, and unanimous decision-making throughout development.

**Skills Applied**  
Mechanical design, prototyping, gear systems, traction analysis, iterative design, sand vehicle engineering, mousetrap mechanics, SolidWorks

`,
    technologies: ['Mechanical Design', 'Prototyping', 'Gear Systems', 'Traction Analysis', 'Iterative Design', 'Sand Vehicle Engineering', 'Mousetrap Mechanics'],
    image: 'https://picsum.photos/400/250?random=8',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'mousetrap-vehicle',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/mousetrap-vehicle/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mousetrap-vehicle/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mousetrap-vehicle/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mousetrap-vehicle/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mousetrap-vehicle/image5.jpeg`
    ]
  },
  {
    id: 9,
    title: 'Crypto Mining Server Manufacturing & GPU Optimization',
    category: 'engineering, business',
    description: 'Manufactured custom servers for cryptocurrency mining operations using restored and optimized GPUs. Designed and built mining rigs with enhanced cooling systems and power management for optimal mining performance.',
    longDescription: `# Crypto Mining Server Manufacturing & GPU Optimization (2019)
**Role:** Hardware Engineer & Operations Manager

**Overview**  
Custom cryptocurrency mining server manufacturing operation specializing in GPU restoration and optimization. Serves mining operations requiring high-performance, cost-effective hardware solutions with enhanced efficiency and reliability.

**Problem / Challenge**  
Commercial mining hardware was expensive and often inefficient. Existing GPU restoration processes lacked systematic optimization for mining performance, resulting in suboptimal hash rates and power consumption.

**Approach / Solution**  
- Sourced GPUs locally and online
- Built new high end servers and servers with used components (better affordability and more popular)
- Often used superseded Xeon processors for affordability with exceptional performance
- Server configuration allowed for ECC RAM which was abundant and affordable
- Pre-assembled with popular spec configurations and supplied occasional custom orders

**Impact / Results**  
- 4 repeat customers
- ~$15,000 in net profit
- Over 30 sales via eBay
- 100% customer satisfaction with no returns requested
- Storefront eventually transformed to a [medical device refurbishment business](/project-detail/jpcommerce-detail) when GPU mining became obsolete

**Skills Applied**  
Hardware manufacturing, GPU optimization, server design, cooling systems, power management, operations management, cryptocurrency mining, hardware assembly

`,
    technologies: ['Hardware Manufacturing', 'GPU Restoration', 'Mining Optimization', 'Server Design', 'Cooling Systems', 'Power Management', 'Cryptocurrency Mining', 'Hardware Assembly'],
    image: 'https://picsum.photos/400/250?random=9',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'crypto-mining-servers',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image13.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image14.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image15.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image16.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image17.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image18.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image19.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/crypto-mining-servers/image20.jpeg`
    ]
  },
  {
    id: 10,
    hidden: true,
    title: 'C6 Corvette Harmonic Balancer Design & Installation',
    category: 'engineering',
    description: 'Designed and installed a custom harmonic balancer for a C6 Corvette engine. Engineered the balancer to reduce engine vibrations and improve performance through precise balancing and proper installation techniques.',
    longDescription: `# C6 Corvette Harmonic Balancer Design & Installation (2021 - 2022)
**Role:** Automotive Engineer & Performance Specialist

**Overview**  
Custom harmonic balancer design and installation for C6 Corvette engine optimization. Serves high-performance automotive applications requiring vibration reduction and engine performance enhancement through precision balancing.

**Problem / Challenge**  
Stock harmonic balancers were insufficient for high-performance applications, causing excessive engine vibrations and reduced performance. Existing aftermarket solutions lacked proper balancing for specific engine configurations.

**Approach / Solution**  
- Designed custom harmonic balancer with precise weight distribution for optimal balancing
- Conducted comprehensive vibration analysis to identify harmonic frequencies
- Implemented precision installation techniques ensuring proper alignment and torque
- Developed performance tuning protocols for maximum engine efficiency

**Skills Applied**
Automotive engineering, harmonic balancing, engine design, vibration analysis, precision installation, performance tuning, Corvette modifications, mechanical assembly

`,
    technologies: ['Automotive Engineering', 'Harmonic Balancing', 'Engine Design', 'Vibration Analysis', 'Precision Installation', 'Performance Tuning', 'Corvette Modifications', 'Mechanical Assembly'],
    image: 'https://picsum.photos/400/250?random=10',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'corvette-harmonic-balancer',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/corvette-harmonic-balancer/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/corvette-harmonic-balancer/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/corvette-harmonic-balancer/image3.jpeg`
    ]
  },
  {
    id: 11,
    title: 'Piano Lyre System & Hammer Mechanism Repair',
    category: 'engineering',
    description: 'Repaired and restored a piano\'s lyre system and hammer mechanism, addressing mechanical issues and ensuring proper functionality. Performed precision adjustments to restore optimal sound quality and mechanical performance.',
    longDescription: `# Piano Lyre System & Hammer Mechanism Repair (2021 - 2022)
**Role:** Precision Instrument Technician & Mechanical Restorer

**Overview**  
Comprehensive piano restoration project focusing on lyre system and hammer mechanism repair. Serves musical instrument restoration requiring precision mechanical work and sound quality optimization.

**Problem / Challenge**  
Piano lyre system and hammer mechanism were malfunctioning, causing poor sound quality and mechanical issues. Existing repair methods lacked precision required for optimal performance restoration.

**Approach / Solution**  
- Diagnosed mechanical issues in lyre system and hammer mechanism
- Performed precision repairs using specialized piano restoration techniques
- Implemented fine-tuning adjustments for optimal sound quality

**Impact / Results**  
- All keys working
- Lyre mechanism completed and functional
- Restored the vintage player mechanism
- Modernized it to work with modern computers

**Skills Applied**  
Piano mechanics, precision repair, mechanical restoration, sound engineering, fine motor skills, instrument maintenance, mechanical assembly, quality control

`,
    technologies: ['Piano Mechanics', 'Precision Repair', 'Mechanical Restoration', 'Sound Engineering', 'Fine Motor Skills', 'Instrument Maintenance', 'Mechanical Assembly', 'Quality Control'],
    image: 'https://picsum.photos/400/250?random=11',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'piano-repair',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/piano-repair/image9.jpeg`
    ]
  },
  {
    id: 12,
    title: 'Complete Boat Restoration & Mechanical Overhaul',
    category: 'engineering',
    description: 'Restored a boat for resale, performing comprehensive repairs including trailer axle restoration, electrical system overhaul, carburetor rebuild, outdrive seal replacement, impeller installation, and U-joint repairs. Successfully restored the vessel to operational condition.',
    longDescription: `# Complete Boat Restoration & Mechanical Overhaul (2020 - 2021)
**Role:** Mechanical Engineer

**Overview**  
Comprehensive boat restoration project involving complete mechanical overhaul and system restoration. Serves marine restoration industry requiring full vessel rehabilitation and mechanical system optimization.

**Problem / Challenge**  
Boat was in non-operational condition with multiple mechanical failures across electrical, propulsion, and trailer systems. Existing restoration methods lacked systematic approach for comprehensive vessel rehabilitation.

**Approach / Solution**  
- Conducted comprehensive system analysis identifying all mechanical failures
- Implemented systematic restoration process across all vessel systems
- Performed electrical system overhaul ensuring safety and reliability
- Executed mechanical repairs including engine, outdrive, and trailer systems

**Leadership & Collaboration**  
- Collaborated with electrical specialists for system overhaul
- Resolved mechanical challenges through systematic diagnosis and repair

**Skills Applied**  
Marine engineering, trailer mechanics, electrical systems, carburetor rebuilding, outdrive maintenance, impeller systems, U-joint repair, marine restoration, mechanical overhaul

`,
    technologies: ['Marine Engineering', 'Trailer Mechanics', 'Electrical Systems', 'Carburetor Rebuilding', 'Outdrive Maintenance', 'Impeller Systems', 'U-Joint Repair', 'Marine Restoration', 'Mechanical Overhaul'],
    image: 'https://picsum.photos/400/250?random=12',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'boat-restoration',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boat-restoration/image13.jpeg`
    ]
  },
  {
    id: 13,
    title: 'BMW M4 G83 Performance Tuning & Custom Modifications',
    category: 'engineering',
    description: 'Completely tuned and modified a BMW M4 G83, performing all work personally including welded downpipes, high-flow catalytic converters, ethanol sensor installation, piggyback tuner setup, custom air intakes, and custom ECU mapping for optimal performance.',
    longDescription: `# BMW M4 G83 Performance Tuning & Custom Modifications (2021 - 2023)
**Role:** Automotive Performance Specialist & ECU Tuner

**Overview**  
Comprehensive BMW M4 G83 performance tuning project involving custom modifications and ECU optimization. Serves high-performance automotive enthusiasts requiring maximum power output and drivability optimization.

**Problem / Challenge**  
Stock BMW M4 G83 performance was limited by factory tuning and restrictive exhaust systems. Existing aftermarket solutions lacked proper integration and optimization for maximum performance gains.

**Approach / Solution**  
- Designed and fabricated custom exhaust system with welded downpipes
- Installed high-flow catalytic converters for improved exhaust flow
- Implemented ethanol sensor system for flexible fuel tuning
- Developed custom ECU mapping for optimal performance and reliability

**Impact / Results**
- 103 WHP gains on 93 octane, 197 WHP gains on E40 mixture (dyno confirmed)
- Up to 18% torque increase
- 10°F average reduction in IAT with installed cold air intake
- 40% maximum tested ethanol mixture compatibility
- 0.4 second improvement on 0-60 launch (from 3.2s to 2.8s)

**Skills Applied**
Automotive tuning, ECU mapping, exhaust systems, catalytic converters, ethanol sensors, piggyback tuners, custom air intakes, performance optimization, BMW modifications, welding

`,
    technologies: ['Automotive Tuning', 'ECU Mapping', 'Exhaust Systems', 'Catalytic Converters', 'Ethanol Sensors', 'Piggyback Tuners', 'Custom Air Intakes', 'Performance Optimization', 'BMW Modifications', 'Welding'],
    image: 'https://picsum.photos/400/250?random=13',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'bmw-m4-tuning',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/bmw-m4-tuning/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/bmw-m4-tuning/image2.jpeg`
    ]
  },
  {
    id: 14,
    title: 'Comprehensive Vehicle Restoration & Resale Side Business',
    category: 'business',
    description: 'Established a profitable vehicle restoration side business sourcing damaged cars and motorcycles from Facebook Marketplace, performing comprehensive restorations, and reselling for significant profit margins. Successfully restored and sold 18 vehicles with an average 15% profit margin.',
    longDescription: `# Comprehensive Vehicle Restoration & Resale Side Business (2020-2026)
**Role:** Founder & Lead Operator

**Overview**  
Automotive restoration venture sourcing damaged vehicles from online marketplaces and reselling after comprehensive restoration. Serves buyers seeking quality restored vehicles at competitive prices while maximizing profit margins through strategic sourcing and operational efficiency.

**Problem / Challenge**  
Market inefficiency between damaged vehicle sellers and restoration buyers created arbitrage opportunities. Vehicle owners lacked restoration expertise and time, while buyers wanted quality restored vehicles without premium dealer markups.

**Approach / Solution**  
- Developed daily systematic AI monitoring for Facebook Marketplace for undervalued vehicles
- Built damage assessment framework to evaluate repair costs vs. market value before acquisition
- Personally diagnosed and repaired vast majority of defects, saving significant costs on labor
- Established supplier relationships for parts procurement and cost optimization

**Impact / Results**  
- 18 vehicles restored and sold across multiple locations in United States
- 15% average profit margin maintained across all transactions
- $132,520 in cumulative sales revenue

**Leadership & Collaboration**  
- Managed end-to-end operations from sourcing to final sale across multiple states
- Coordinated with mechanics, body shops, and parts suppliers for quality restoration
- Resolved quality control conflicts through standardized inspection protocols
- Built reputation through high customer satisfaction

**Skills Applied**  
Market analysis, pricing strategy, quality control, financial management, automotive engineering, Excel

`,
    technologies: ['Automotive Restoration', 'Motorcycle Mechanics', 'Engine Rebuilding', 'Business Development', 'Market Analysis', 'Profit Optimization', 'Quality Control', 'Mechanical Engineering', 'Parts Sourcing', 'Entrepreneurship', 'Facebook Marketplace', 'Vehicle Diagnostics', 'Body Work', 'Paint & Finishing', 'Financial Management'],
    image: `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/snow.png`,
    github: null,
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'vehicle-restoration-business',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/snow.png`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image13.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image14.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image15.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/vehicle-restoration-business/image16.jpeg`
    ]
  },
  {
    id: 15,
    title: 'Custom Fabricated Closet with Engineered Wood',
    category: 'engineering',
    description: 'Designed and constructed a custom fabricated closet system using engineered wood materials. Applied woodworking principles and fabrication techniques to create a functional and aesthetically pleasing storage solution.',
    longDescription: `# Custom Fabricated Closet with Engineered Wood (2020 - 2021)
**Role:** Custom Fabrication Specialist & Design Engineer

**Overview**  
Custom closet system design and fabrication using engineered wood materials. Serves residential and commercial clients requiring functional, aesthetically pleasing storage solutions with optimal space utilization.

**Problem / Challenge**  
Standard closet systems lacked customization for specific space requirements and aesthetic preferences. Existing solutions were expensive and often poorly fitted to unique room dimensions.

**Approach / Solution**  
- Designed custom closet system optimized for specific space dimensions
- Selected engineered wood materials for optimal strength and cost efficiency
- Implemented precision cutting and assembly techniques for perfect fit

**Skills Applied**  
Woodworking, engineered wood, custom fabrication, carpentry, design engineering, material selection, precision cutting, assembly techniques, interior design, structural planning

`,
    technologies: ['Woodworking', 'Engineered Wood', 'Custom Fabrication', 'Carpentry', 'Design Engineering', 'Material Selection', 'Precision Cutting', 'Assembly Techniques', 'Interior Design', 'Structural Planning'],
    image: 'https://picsum.photos/400/250?random=15',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'custom-closet',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/custom-closet/image8.jpeg`
    ]
  },
  {
    id: 16,
    title: 'Mercedes E300 Rack and Pinion Steering System Replacement',
    category: 'engineering',
    description: 'Replaced the rack and pinion steering system on a Mercedes E300, performing comprehensive diagnostics, removal of the old system, and installation of the new steering components. Ensured proper alignment and functionality.',
    longDescription: `# Mercedes E300 Rack and Pinion Steering System Replacement (2021 - 2022)
**Role:** Automotive Systems Specialist & Precision Technician

**Overview**  
Comprehensive Mercedes E300 steering system replacement project involving rack and pinion system overhaul. Serves automotive repair industry requiring precision steering system maintenance and replacement.

**Problem / Challenge**  
Mercedes E300 steering system was malfunctioning with poor responsiveness and alignment issues. Existing repair methods lacked precision required for proper steering geometry and functionality.

**Approach / Solution**  
- Conducted comprehensive diagnostics to identify steering system failures
- Performed precision removal of old rack and pinion system
- Installed new steering components with proper alignment procedures
- Implemented quality control processes ensuring optimal functionality

**Skills Applied**
Automotive engineering, steering systems, rack and pinion, Mercedes diagnostics, precision installation, alignment procedures, automotive repair, steering geometry, quality control, mechanical assembly

`,
    technologies: ['Automotive Engineering', 'Steering Systems', 'Rack and Pinion', 'Mercedes Diagnostics', 'Precision Installation', 'Alignment Procedures', 'Automotive Repair', 'Steering Geometry', 'Quality Control', 'Mechanical Assembly'],
    image: 'https://picsum.photos/400/250?random=16',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'mercedes-steering',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/mercedes-steering/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mercedes-steering/image2.jpeg`
    ]
  },
  {
    id: 17,
    title: 'Luxury Dining Table Design & SolidWorks Modeling',
    category: 'engineering',
    description: 'Designed and modeled a premium dining table inspired by Restoration Hardware aesthetics using SolidWorks CAD software. Applied advanced 3D modeling techniques, material selection analysis, and structural engineering principles to create a sophisticated furniture design.',
    longDescription: `# Luxury Dining Table Design & SolidWorks Modeling (2020 - 2021)
**Role:** Product Design Engineer & CAD Specialist

**Overview**  
Premium dining table design project using SolidWorks CAD software with Restoration Hardware aesthetic inspiration. Serves luxury furniture industry requiring sophisticated design and engineering solutions.

**Problem / Challenge**  
Existing dining table designs lacked the sophisticated aesthetic and structural engineering required for luxury furniture. Commercial solutions were expensive and often poorly engineered for long-term durability.

**Approach / Solution**  
- Designed premium dining table with Restoration Hardware aesthetic inspiration
- Applied advanced 3D CAD modeling techniques for precise design visualization
- Conducted material selection analysis for optimal strength and aesthetics
- Implemented structural engineering principles ensuring long-term durability

**Skills Applied**
SolidWorks, 3D CAD modeling, product design, structural engineering, material analysis, furniture design, design engineering, 3D visualization, engineering documentation, luxury product development

`,
    technologies: ['SolidWorks', '3D CAD Modeling', 'Product Design', 'Structural Engineering', 'Material Analysis', 'Furniture Design', 'Design Engineering', '3D Visualization', 'Engineering Documentation', 'Luxury Product Development'],
    image: 'https://picsum.photos/400/250?random=17',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'luxury-dining-table',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/luxury-dining-table/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/luxury-dining-table/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/luxury-dining-table/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/luxury-dining-table/image4.jpeg`
    ]
  },
  {
    id: 18,
    title: 'Heavy-Duty Boulder Transport Device: CAD Design & Prototype Development',
    category: 'engineering',
    description: 'Engineered a mechanical device capable of transporting 500lb boulders across uneven terrain. Utilized SolidWorks for comprehensive CAD design, implemented systematic prototype development (low-medium-high fidelity), and conducted rigorous stress testing to ensure structural integrity and operational reliability.',
    longDescription: `# Heavy-Duty Boulder Transport Device: CAD Design & Prototype Development (2021 - 2022)
**Role:** Heavy Equipment Design Engineer & Prototype Developer

**Overview**  
Heavy-duty boulder transport device designed for 500lb capacity across uneven terrain. Serves construction and landscaping industries requiring efficient heavy material handling solutions with terrain adaptability.

**Problem / Challenge**  
Existing material handling equipment was insufficient for heavy boulder transport across uneven terrain. Manual methods were dangerous and inefficient, requiring innovative mechanical solutions.

**Approach / Solution**  
- Designed heavy-duty transport device using SolidWorks CAD for 500lb capacity
- Implemented systematic prototype development from low to high fidelity
- Conducted rigorous stress analysis ensuring structural integrity
- Developed terrain adaptation mechanisms for uneven surface navigation

**Impact / Results**  
- Defined four validation criteria for client delivery: lifting capability, maneuverability, lifting height, and durability (cost modeled separately)
- Committed to final wheelbarrow design using a pulley system to center rock load above the main wheel for one-person operation
- Lifting tests validated securing varied loads (20–50 lb scaled prototype objects, sand-filled baskets, and natural rocks) without slippage or component failure
- Maneuverability targets: travel 50+ yards over rough terrain and 4–5% slopes; complete 100 ft routes in under 2 minutes with no more than 2 redirects and no tip-overs across grass, gravel, and concrete
- Lifting height target: raise rocks up to 300 lbs at least 2 feet for wall placement, with isolated component testing (rollers, straps, wood) at 50 lb intervals to 300+ lbs
- Durability plan for 1:2 medium-fidelity prototype: 175 lb scaled loads, 100 winch cycles, 100 terrain trials over 50 yards, and overnight static load testing before full-scale build

**Leadership & Collaboration**  
- Led heavy equipment design team through CAD modeling and prototyping
- Collaborated with structural engineers for optimal load distribution
- Resolved terrain adaptation challenges through iterative design
- Implemented comprehensive testing protocols ensuring safety standards

**Skills Applied**  
SolidWorks CAD, heavy equipment design, prototype development, stress analysis, structural engineering, mechanical design, load testing, terrain adaptation, engineering validation, product development lifecycle

`,
    technologies: ['SolidWorks CAD', 'Heavy Equipment Design', 'Prototype Development', 'Stress Analysis', 'Structural Engineering', 'Mechanical Design', 'Load Testing', 'Terrain Adaptation', 'Engineering Validation', 'Product Development Lifecycle'],
    image: 'https://picsum.photos/400/250?random=18',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'boulder-transport-device',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/boulder-transport-device/image6.jpeg`
    ]
  },
  {
    id: 19,
    title: 'McDonald\'s Happy Meal Toy: Injection Moldable Design & Safety Compliance',
    category: 'engineering',
    description: 'Designed and developed an injection moldable toy meeting strict McDonald\'s Happy Meal design limitations and safety standards. Utilized SolidWorks for precise CAD modeling, implemented 3D printing for rapid prototyping, and ensured compliance with child safety regulations and manufacturing requirements.',
    longDescription: `# McDonald's Happy Meal Toy: Injection Moldable Design & Safety Compliance (2021 - 2022)
**Role:** Product Design Engineer & Safety Compliance Specialist

**Overview**  
Injection moldable toy design meeting strict McDonald's Happy Meal specifications and child safety standards. Serves toy manufacturing industry requiring compliance with safety regulations and manufacturing constraints.

**Problem / Challenge**  
McDonald's Happy Meal toys required strict design limitations and safety compliance while maintaining manufacturability. Existing designs often failed safety standards or manufacturing feasibility requirements.

**Approach / Solution**  
- Designed injection moldable toy using SolidWorks CAD for precise modeling
- Implemented 3D printing for rapid prototyping and design validation
- Ensured compliance with child safety regulations and standards
- Developed manufacturing design optimized for injection molding process

**Leadership & Collaboration**  
- Led product design team through safety compliance and manufacturing phases
- Collaborated with safety specialists for optimal child safety standards
- Resolved manufacturing challenges through design optimization
- Implemented quality assurance processes ensuring compliance standards

**Skills Applied**  
SolidWorks CAD, injection molding design, 3D printing, product safety standards, child safety compliance, rapid prototyping, manufacturing design, regulatory compliance, toy design engineering, quality assurance

`,
    technologies: ['SolidWorks CAD', 'Injection Molding Design', '3D Printing', 'Product Safety Standards', 'Child Safety Compliance', 'Rapid Prototyping', 'Manufacturing Design', 'Regulatory Compliance', 'Toy Design Engineering', 'Quality Assurance'],
    image: 'https://picsum.photos/400/250?random=19',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'mcdonalds-toy',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/mcdonalds-toy/image8.jpeg`
    ]
  },
  {
    id: 20,
    title: 'Metal & Woodworking Workshop Construction',
    category: 'engineering',
    description: 'Designed and constructed a comprehensive metal and woodworking workshop that complies with all building codes and regulations. The project included electrical configuration, lighting design, base construction with proper compaction, and waterproofing systems for a durable and safe working environment.',
    longDescription: `# Metal & Woodworking Workshop Construction (2020 - 2021)
**Role:** Construction Project Manager & Building Systems Engineer

**Overview**  
Comprehensive workshop construction project creating professional-grade metal and woodworking facility. Serves construction industry requiring full building code compliance and specialized workshop infrastructure.

**Problem / Challenge**  
Existing workshop facilities lacked proper building code compliance and specialized infrastructure for metal and woodworking operations. Safety and regulatory requirements were not met in standard construction approaches.

**Approach / Solution**  
- Designed comprehensive workshop layout with dedicated work zones
- Implemented electrical system with 200-amp service and GFCI protection
- Conducted soil analysis and proper compaction for foundation integrity
- Developed waterproofing and ventilation systems for optimal working conditions

**Skills Applied**
Construction management, building codes compliance, electrical design, lighting systems, foundation engineering, soil compaction, waterproofing systems, workshop design, safety standards, project planning

`,
    technologies: ['Construction Management', 'Building Codes Compliance', 'Electrical Design', 'Lighting Systems', 'Foundation Engineering', 'Soil Compaction', 'Waterproofing Systems', 'Workshop Design', 'Safety Standards', 'Project Planning', 'Concrete Engineering', 'Drainage Systems', 'Ventilation Design', 'Code Inspection', 'Quality Control'],
    image: 'https://picsum.photos/400/250?random=20',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'workshop-construction',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/workshop-construction/image8.jpeg`
    ]
  },
  {
    id: 21,
    title: 'VisionAssist - AI-Powered Object Recognition for Visually Impaired',
    category: 'software',
    description: 'Developed an Android application using TensorFlow and real-time object recognition to help visually impaired users locate and identify objects in their environment through their phone\'s camera.',
    longDescription: `# VisionAssist - AI-Powered Object Recognition for Visually Impaired (2019)
**Role:** Mobile AI Developer & Accessibility Specialist

**Overview**  
Android application using TensorFlow Lite for real-time object recognition to assist visually impaired users. Serves accessibility technology market requiring innovative solutions for independent navigation and object identification.

**Problem / Challenge**  
Visually impaired individuals lacked real-time assistance for object identification and navigation. Existing solutions were slow, inaccurate, or not accessible, limiting independence and safety.

**Approach / Solution**  
- Developed Android app using TensorFlow Lite for real-time object recognition
- Implemented computer vision algorithms for 30 FPS processing
- Created audio feedback system with customizable sensitivity settings
- Integrated accessibility features including voice commands and haptic feedback

**Impact / Results**  
- Built as a pre-college, self-directed learning project to explore computer vision and real-time object identification — not intended for production deployment.
- Delivered a working Android (Java) prototype that runs a quantized TensorFlow Lite SSD MobileNet model fully on-device.
- Performed live camera-based detection across the 80 everyday COCO object categories, drawing labeled bounding boxes in real time.
- Configured a 300×300 input pipeline with a 0.5 confidence threshold, gaining hands-on experience with model inputs, quantization, and inference tradeoffs on mobile hardware.
- Deepened practical understanding of the end-to-end CV workflow — camera capture, preprocessing, on-device inference, and rendering — as a foundation for later college coursework.

**Skills Applied**  
Android development, TensorFlow Lite, computer vision, machine learning, real-time processing, accessibility design, Java/Kotlin, OpenCV, audio processing, camera integration

**Selected Artifacts**  
[Code Repository](https://github.com/Jaiparmar940/VisionAI)

`,
    technologies: ['Android Development', 'TensorFlow Lite', 'Computer Vision', 'Machine Learning', 'Real-time Processing', 'Accessibility Design', 'Java/Kotlin', 'OpenCV', 'Audio Processing', 'Camera Integration', 'Mobile AI', 'Object Detection', 'Neural Networks', 'User Experience Design', 'Assistive Technology'],
    image: 'https://picsum.photos/400/250?random=21',
    github: 'https://github.com/Jaiparmar940/VisionAI',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'visionassist-app',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/preview-5.webp`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/visionassist-app/image8.jpeg`
    ]
  },
  {
    id: 22,
    hidden: true,
    title: 'Hackerspace - Public Workshop Construction & Community Fabrication Lab',
    category: 'engineering',
    description: 'Designed and constructed a public workshop facility called "Hackerspace" in collaboration with a local fabrication shop, creating a community resource for makers, engineers, and entrepreneurs to access professional-grade tools and equipment.',
    longDescription: `# Hackerspace - Public Workshop Construction & Community Fabrication Lab (2020 - 2022)
**Role:** Community Development Manager & Workshop Design Engineer

**Overview**  
Public workshop facility construction project creating community resource for makers, engineers, and entrepreneurs. Serves community development initiatives requiring accessible professional-grade tools and collaborative workspace.

**Problem / Challenge**  
Local makers and entrepreneurs lacked access to professional-grade tools and equipment. Individual ownership was cost-prohibitive, limiting innovation and skill development in the community.

**Approach / Solution**  
- Designed comprehensive workshop layout with specialized work zones
- Collaborated with local fabrication shop for equipment and expertise
- Implemented safety systems and training programs for community use
- Developed resource sharing model for sustainable community access

**Impact / Results**  
<!-- - <community_access>% improvement in community access to professional tools -->
<!-- - <safety_standards>% improvement in safety standards and training -->
<!-- - <collaboration_opportunities>% increase in collaborative project opportunities -->
<!-- - <skill_development>% improvement in community skill development -->
<!-- - <resource_utilization>% optimization in tool and equipment utilization -->
- Currently under construction

**Leadership & Collaboration**  
- Led community development team through facility design and construction
- Collaborated with local fabrication shop for equipment and expertise
- Resolved community access challenges through innovative resource sharing
- Implemented safety protocols ensuring community workshop standards

**Skills Applied**  
Community development, workshop design, fabrication shop collaboration, safety systems design, tool library management, community outreach, project planning, resource sharing

`,
    technologies: ['Community Development', 'Workshop Design', 'Fabrication Shop Collaboration', 'Safety Systems Design', 'Tool Library Management', 'Community Outreach', 'Project Planning', 'Resource Sharing', 'Workshop Layout', 'Safety Training Programs', 'Digital Fabrication', '3D Printing Integration', 'Electronics Workstations', 'Metalworking Equipment', 'Woodworking Tools'],
    image: 'https://picsum.photos/400/250?random=22',
    github: null,
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'hackerspace-workshop',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/hackerspace-workshop/image8.jpeg`
    ]
  },
  {
    id: 23,
    hidden: true,
    title: 'Project Pure - Clean Water Initiative for Central America',
    category: 'charity',
    description: 'Developed and implemented a comprehensive clean water solution for Central American communities using coagulating agents, chlorine tablets, rainwater collection systems, and well construction to provide sustainable access to safe drinking water.',
    longDescription: `# Project Pure - Clean Water Initiative for Central America (2020 - 2023)
**Role:** Humanitarian Engineer & Community Development Specialist

**Overview**  
Comprehensive clean water solution for Central American communities using multiple purification technologies. Serves humanitarian development initiatives requiring sustainable water access solutions for underserved communities.

**Problem / Challenge**  
Central American communities lacked access to clean, safe drinking water, leading to widespread waterborne diseases. Existing solutions were expensive, unsustainable, or not culturally appropriate for local implementation.

**Approach / Solution**  
- Developed multi-technology water purification system using coagulating agents
- Implemented chlorine disinfection and rainwater harvesting systems
- Built community-centered approach with local training and maintenance
- Designed well construction systems for groundwater access

**Impact / Results**  
- <water_access>% improvement in clean water access for target communities
- <disease_reduction>% reduction in waterborne diseases
- <sustainability>% improvement in long-term system sustainability
- <community_empowerment>% improvement in community self-sufficiency
- <cost_effectiveness>% reduction in water access costs

**Leadership & Collaboration**  
- Led humanitarian engineering team through community development phases
- Collaborated with local communities for culturally appropriate solutions
- Resolved implementation challenges through community-centered approach
- Implemented training programs ensuring long-term sustainability

**Skills Applied**  
Water purification, coagulation technology, chlorine disinfection, rainwater harvesting, well construction, community development, sustainable engineering, water quality testing, local materials, solar pumping

`,
    technologies: ['Water Purification', 'Coagulation Technology', 'Chlorine Disinfection', 'Rainwater Harvesting', 'Well Construction', 'Community Development', 'Sustainable Engineering', 'Water Quality Testing', 'Local Materials', 'Solar Pumping', 'First-Flush Systems', 'Storage Tank Design', 'Distribution Networks', 'Community Training', 'Monitoring Systems'],
    image: 'https://picsum.photos/400/250?random=23',
    github: null,
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'project-pure',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/project-pure/image10.jpeg`
    ]
  },
  {
    id: 24,
    title: 'Custom CPU Architecture - Single-Cycle Processor Design in Logisim',
    category: 'engineering',
    description: 'Designed and implemented a fully functional single-cycle CPU using Logisim, featuring a complete instruction set architecture with ALU operations, memory management, branching logic, and I/O capabilities for keyboard input and teletype output.',
    longDescription: `# Custom CPU Architecture - Single-Cycle Processor Design in Logisim (2022 - 2023)
**Role:** Computer Architecture Engineer & Digital Logic Designer

**Overview**  
Custom single-cycle CPU design using Logisim digital logic simulator with complete instruction set architecture. Serves computer engineering education and digital systems design requiring comprehensive processor implementation.

**Problem / Challenge**  
Existing CPU design tools lacked comprehensive instruction set implementation and I/O capabilities. Educational resources needed practical examples of complete processor design from first principles.

**Approach / Solution**  
- Implemented the Duke 250/16 single-cycle, 16-bit MIPS-like RISC processor in Logisim Evolution
- Built Harvard architecture with separate instruction and data memory, plus full datapath and control logic
- Designed 8-register file ($r0–$r7) with $r0 hardwired to zero and $r7 as link register
- Added keyboard input and teletype output with correct reset and clock edge behavior

**Impact / Results**  
- Implemented full Duke 250/16 instruction set: arithmetic, logic, shifts, memory access, branching, jumping, and I/O
- Built Harvard architecture with separate instruction/data memory and register file using tri-state buffers and decoder
- Achieved correct asynchronous reset for PC, registers, TTY, and keyboard buffer with spec-compliant clocking
- Passed automated verification via 'hwtest.py' against provided assembler and simulator output
- Delivered complete Logisim design ('cpu.circ') for ECE 250 processor core coursework

**Leadership & Collaboration**  
- Led computer architecture team through design and implementation phases
- Collaborated with digital logic specialists for optimal circuit design
- Resolved integration challenges through systematic design approach
- Implemented comprehensive testing protocols ensuring functionality

**Skills Applied**  
Digital logic design, computer architecture, Logisim, CPU design, instruction set architecture, digital circuits, control logic, memory systems, ALU design, register files

**Selected Artifacts**  
[Code Repository](https://github.com/Jaiparmar940/ECE250Proj4)

`,
    technologies: ['Digital Logic Design', 'Computer Architecture', 'Logisim', 'CPU Design', 'Instruction Set Architecture', 'Digital Circuits', 'Control Logic', 'Memory Systems', 'ALU Design', 'Register Files', 'Branching Logic', 'I/O Systems', 'Digital Electronics', 'Computer Engineering', 'Hardware Design'],
    image: 'https://picsum.photos/400/250?random=24',
    github: 'https://github.com/Jaiparmar940/ECE250Proj4',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'custom-cpu-logisim',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/custom-cpu-logisim/image1.jpeg`
    ]
  },
  {
    id: 25,
    title: 'Autonomous Surgical Robotic Head Swapper',
    category: 'engineering, robotics, embedded systems',
    description: 'Built firmware and web UI for a 5-DOF robotic arm using three ESP32 boards coordinated via UART. Features synchronized stepper control (TB6600), limit switch homing, hot-swappable tools (electromagnet, vacuum, circular saw), WebSocket/HTTP dashboard, sequence programming, OTA updates, and Web Serial interface.',
    longDescription: `# Autonomous Surgical Robotic Head Swapper — Multi-Board ESP32 Control (Spring 2026)
**Role:** Lead Embedded Systems & Robotics Engineer

**Overview**  
Developed a complete distributed control system for a 5-degree-of-freedom robotic arm with interchangeable tool heads. Three ESP32 boards (Main, Sensor, MOSFET) communicate over UART to manage 6 stepper motors, multiple sensors, power switching for tools, WiFi-enabled web interface, and precise motion sequences. The system supports hot-swapping tools via an 8-pin magnetic wrist connector.

**Problem / Challenge**  
Implementing the 8 specific requirements for the ME424 final project required coordinating real-time motor control, sensor feedback, tool actuation, and a rich user interface across multiple microcontrollers. Challenges included reliable UART inter-board communication, synchronized multi-axis motion with ramping, tool identification and power management, OTA updates, and building an intuitive web-based HMI using Web Serial and WebSockets while meeting real-time constraints and safety requirements.

**Approach / Solution**  
- Architected master-slave system: Main board (WiFi, HTTP UI, WebSocket, OTA, stepper drivers via TB6600 for 6 axes with dual synced motors on Stage 2), Sensor board (limit switches, Hall sensor for Stage 5, tool ID via ADC), MOSFET board (power control for MAG/VAC/SAW with PWM for BLDC driver)
- Implemented robust command protocols over UART (115200 baud) with ACKs, state reporting, and error handling
- Developed advanced motion commands (syncabs, homing sequences home sN, soft zeroing, ramping, max speed control) for precise synchronized playback
- Built full-featured web app supporting manual jogging, pose recording (all 6 axes at once), sequence composition (poses + delays + MOSFET actions), localStorage/JSON persistence, auto-homing
- Integrated analog tool ID, limit switch logic, comprehensive debugging via serial and web dashboard
- Created Wokwi simulation model and extensive documentation for reproducibility

**Impact / Results**  
- Successfully completed all 8 ME424 UI and control requirements
- Achieved reliable synchronized 5-DOF motion with configurable speed profiles and safety homing
- Enabled rapid prototyping and iteration through web UI, OTA firmware updates, and simulation
- Demonstrated professional-grade embedded systems engineering with multi-board architecture, real-time control, and user-friendly HMI
- Produced well-documented, modular codebase suitable for extension or demonstration

**Leadership & Collaboration**  
- Owned end-to-end system design across mechanical integration, electrical, firmware, UI, and documentation
- Designed and iterated on custom inter-board communication protocols based on hardware testing
- Created comprehensive README with pinouts, architecture diagrams, upload instructions, and protocol specs
- Balanced performance, reliability, safety, and usability across complex distributed real-time system

**Skills Applied**  
Embedded C++, ESP32, PlatformIO, UART protocols, Stepper motor control, TB6600 drivers, MOSFET & PWM control, WebSocket, HTTP server, Web Serial API, OTA updates, real-time systems, robotics, mechatronics, motion planning, sensor integration, technical documentation, HMI development

**Selected Artifacts**  
- [GitHub Repo](https://github.com/Jaiparmar940/me424-robot)
- [App Interface](https://github.com/Jaiparmar940/me424-robot/tree/main/app)
- [Full Documentation](https://github.com/Jaiparmar940/me424-robot/blob/main/README.md)

`,
    technologies: ['ESP32', 'C++', 'PlatformIO', 'UART', 'Stepper Motors', 'TB6600', 'WebSocket', 'Web Serial', 'OTA', 'MOSFET', 'PWM', 'Robotics', 'Mechatronics', 'Embedded Systems', 'Motion Control', 'Real-time Programming'],
    image: `${process.env.PUBLIC_URL}/static/projects/me424-robot/demo.gif`,
    github: 'https://github.com/Jaiparmar940/me424-robot',
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'autonomous-surgical-robot',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/demo.gif`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/me424-robot/image8.jpeg`
    ]
  },
  {
    id: 26,
    title: 'Grid Intelligence - Energy Market Forecasting & Battery Dispatch Optimization Platform',
    category: 'energy, software, machine learning, finance',
    description: 'Energy intelligence platform using congestion forecasting, LMP prediction, and optimization algorithms to maximize BESS arbitrage profits while enhancing grid reliability. Built ML pipeline with TFT, XGBoost, RL, and constrained optimization across 7k+ nodes and 100M+ rows of data.',
    longDescription: `# Grid Intelligence - Energy Market Forecasting & Battery Dispatch Optimization Platform (Jan 2026 - May 2026)

**Role:** Technical Lead

**Overview**  
Worked directly on the architecture, market strategy, and machine-learning pipeline for an energy intelligence platform focused on optimizing battery energy storage systems (BESS) in wholesale electricity markets. The platform uses congestion forecasting, locational price prediction, and optimization algorithms to identify profitable charging and discharging opportunities while improving grid reliability.

**Key Contributions**  
- Researched and modeled wholesale electricity market mechanics, including PJM nodal pricing, congestion dynamics, negative pricing events, renewable curtailment, and battery arbitrage opportunities.  
- Designed an end-to-end forecasting pipeline combining market data, weather forecasts, renewable generation forecasts, and transmission constraints to predict locational marginal prices (LMPs) and grid congestion events.  
- Developed forecasting architecture using machine learning techniques including Temporal Fusion Transformers (TFT), LightGBM, XGBoost, quantile forecasting, and probabilistic congestion modeling.    
- Designed battery dispatch optimization frameworks using constrained optimization, reinforcement learning, and genetic algorithm approaches to maximize energy arbitrage profits while accounting for battery degradation, efficiency losses, and operational constraints.  
- Helped define the company’s product roadmap, positioning Grid Intelligence as a congestion-native intelligence layer spanning forecasting, dispatch recommendations, and financial congestion risk analytics.  
- Conducted competitive analysis across major industry platforms including Tesla Autobidder, Fluence Mosaic, Tyba, Yes Energy, Wood Mackenzie, and S&P Global to identify market gaps and differentiation opportunities.  
- Created system architecture for data ingestion, feature engineering, probabilistic forecasting, dispatch recommendation generation, dashboard visualization, and API-based integrations.  

**Technical Highlights**  
- Electricity market forecasting
- Locational Marginal Pricing (LMP) modeling
- Battery Energy Storage Systems (BESS)
- Optimization & Operations Research
- Time-series machine learning
- Congestion forecasting
- Reinforcement Learning (PPO/DQN)
- Quantile prediction & uncertainty estimation
- Energy market analytics
- Product strategy and competitive intelligence

**Problem / Challenge**  
Wholesale electricity markets are volatile with complex nodal pricing, transmission congestion, renewable variability, and battery operational constraints. Battery storage operators lacked accurate localized forecasts and optimization tools to capitalize on arbitrage opportunities while supporting grid reliability and decarbonization.

**Approach / Solution**  
- Built market-mechanics research foundation and end-to-end data pipeline for multi-source forecasting (market, weather, renewables, transmission).
- Implemented state-of-the-art ML (TFT, tree-based ensembles, quantile/probabilistic models) and advanced optimization (constrained solvers, RL with PPO/DQN, genetic algorithms).
- Designed full system architecture with feature engineering, dashboard, API integrations, and competitive positioning.
- Balanced technical model performance with business metrics (arbitrage profits, degradation costs, regulatory compliance).

**Impact / Results**  
- Robust forecasting and dispatch models capable of operating across 7,000+ nodes with 100M+ training rows.
- Secured $100k in non-dilutive funding through demonstrated technical architecture and market differentiation.
- Positioned the platform as a congestion-native intelligence layer against competitors like Tesla Autobidder and Fluence.
- Contributed to improved grid reliability through better BESS utilization in wholesale markets.

**Leadership & Collaboration**  
- Led technical architecture and ML development for early-stage startup with Duke peers.
- Defined product roadmap and conducted in-depth competitive analysis to carve differentiation.
- Collaborated on market strategy while ensuring models were production-ready and explainable.

**Skills Applied**  
Electricity market forecasting, LMP modeling, BESS optimization, Temporal Fusion Transformers, XGBoost/LightGBM, Reinforcement Learning (PPO/DQN), Quantile Forecasting, Congestion Analysis, Operations Research, Time-Series ML, Product Strategy, Competitive Intelligence, Python, Energy Analytics

**Selected Artifacts**  
[Technical Documentation](https://github.com/Jaiparmar940/grid-intelligence)
`,
    technologies: ['Energy Markets', 'LMP Modeling', 'BESS', 'Temporal Fusion Transformers', 'XGBoost', 'LightGBM', 'Reinforcement Learning', 'PPO', 'Quantile Forecasting', 'Congestion Analysis', 'Operations Research', 'Python', 'Product Strategy'],
    image: `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image1.jpeg`,
    github: 'https://github.com/Jaiparmar940/grid-intelligence',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'grid-intelligence',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image4.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image5.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image6.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image7.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image8.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image9.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image10.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image11.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image12.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image13.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image14.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image15.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image16.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image17.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image18.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image19.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/grid-intelligence/image20.jpeg`
    ]
  },
  {
    id: 27,
    title: 'Omega Portfolio Engine - Regime-Aware Quantitative Optimization Platform',
    category: 'finance, software, machine learning',
    description: 'Regime-aware portfolio optimization engine with GMV and Omega ratio objectives, HMM/LSTM/GMM regime detection, walk-forward backtesting, custom policy management, Streamlit UI, FastAPI backend, risk constraints (CVaR, turnover, sectors), and Yahoo Finance integration.',
    longDescription: `# Omega Portfolio Engine — Regime-Aware Quantitative Optimization Platform (2025)

**Role:** Quantitative Research Analyst & ML Engineer

**Overview**  
A regime-aware, client-customizable portfolio optimization engine that detects market regimes and optimizes portfolios for Global Minimum Variance (GMV) and Omega ratio objectives. Features interactive Streamlit web UI, FastAPI backend, comprehensive walk-forward backtesting, policy management, risk constraints, and multiple data sources. Serves quantitative investors and portfolio managers seeking data-driven, regime-adaptive asset allocation.

**Problem / Challenge**  
Traditional portfolio optimization (e.g. mean-variance) fails to account for changing market regimes, leading to poor out-of-sample performance. Investors needed tools that dynamically detect regimes (via HMM, LSTM, GMM), incorporate custom policies and risk constraints (CVaR, turnover, sector limits), support robust backtesting, and provide an intuitive interface for real decision-making.

**Approach / Solution**  
- Implemented regime detection using Hidden Markov Models, LSTMs, and Gaussian Mixture Models with automated feature extraction from price and macro data.
- Built dual-objective optimizers for GMV and Omega ratio with policy enforcement and risk constraints.
- Developed full walk-forward backtesting engine with performance attribution, visualization, and fallback logic.
- Created modern web interface (Streamlit) for portfolio upload, policy creation/editing, optimization, backtesting, and results dashboard; paired with FastAPI backend for API/CLI access.
- Integrated Yahoo Finance, CSV, and sample data loaders with robust error handling and session management.
- Added comprehensive testing, pre-commit hooks, and documentation for production readiness.

**Impact / Results**  
- Production-ready platform with interactive UI enabling rapid iteration on portfolios and policies.
- Robust backtesting and regime-aware optimization that outperforms static approaches in varying market conditions.
- Centralized results dashboard and export capabilities for analysis and client reporting.
- Easy deployment via make start with full support for research notebooks, API, and web workflows.
- Inspired by Bridgewater All Weather strategy; extensible for ESG, multi-currency, and real-time use cases.

**Leadership & Collaboration**  
- Owned full-stack implementation from data pipeline and ML models to UI/UX and deployment.
- Collaborated with quant finance principles to ensure models were practical and explainable.
- Iterated on user feedback to refine the interface for both technical and non-technical users.
- Maintained high code quality with ruff, black, mypy, pytest, and pre-commit.

**Skills Applied**  
Quantitative Finance, Portfolio Optimization, Regime Detection (HMM/LSTM/GMM), Walk-Forward Backtesting, Risk Management (CVaR, Turnover), Machine Learning, Streamlit, FastAPI, Python, Pandas, Feature Engineering, Policy Management, Backtesting Frameworks, Data Visualization, API Development

**Selected Artifacts**  
[initial brainstorming public repository](https://github.com/Jaiparmar940/allweather-quant-lab)

`,
    technologies: ['Python', 'Streamlit', 'FastAPI', 'Pandas', 'HMM', 'LSTM', 'GMM', 'GMV Optimization', 'Omega Ratio', 'Walk-Forward Backtesting', 'CVaR', 'Regime Detection', 'Quantitative Finance', 'Risk Management'],
    image: 'https://picsum.photos/400/250?random=27',
    github: 'https://github.com/Jaiparmar940/allweather-quant-lab',
    live: null,
    featured: true,
    hasDetailPage: true,
    detailSlug: 'omega-portfolio-engine',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/omega-portfolio-engine/image1.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/omega-portfolio-engine/image2.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/omega-portfolio-engine/image3.jpeg`,
      `${process.env.PUBLIC_URL}/static/projects/omega-portfolio-engine/image4.jpeg`
    ]
  },
  {
    id: 28,
    title: 'no-start-env — Physical-Systems Diagnostic Benchmark for LLM Agents',
    category: 'machine learning, RL environments, evals',
    description: 'A simulated vehicle electrical no-start environment where LLM agents diagnose faults with realistic tools — measurements, cranks, part swaps — scored by a cheat-resistant grader. Built on Inspect AI. 9 models benchmarked across 5 fault scenarios, 225 episodes; best-to-worst spread of 78.8 points.',
    longDescription: `# no-start-env — Physical-Systems Diagnostic Benchmark for LLM Agents (2026)
**Role:** Founder & sole author — current full-time work

**What it is**
A reinforcement-learning environment and published evaluation for physical-world diagnostic reasoning. The agent gets a vehicle that won't start and the tools a technician would have — voltage measurements, crank attempts, visual inspection, CAN bus reads, part replacement — and has to find the root cause, fix it, and verify the fix. Built on Inspect AI so labs can run it directly. This is the first public artifact of [Second Nature](https://snlabs.dev), my company that builds RL environments for physical decision processes.

**Why a diagnostic benchmark**
The knowledge that separates a good technician from a parts-swapper is tacit: which measurement to take first, when a plausible reading is a red herring, when a fix is actually verified. That knowledge never makes it into training data, and models trained on text guess plausibly instead of measuring. The grader is built so plausible guessing loses:

- Scoring: root cause 60 / parts discipline 25 / cost efficiency 15
- Replacing parts before any measurement caps the score at 40
- Full credit requires the root-cause part replaced and a successful start verifying the repair
- Each wrong part replaced costs 8 points
- Reliability is reported as pass^k — full root-cause credit in every epoch, because a model that is right two times out of three is not deployable

**Results (July 2026)**
- 9 models × 5 fault scenarios × 5 epochs = 225 episodes, all on uncoached prompts
- Best-to-worst spread: 78.8 points (top model mean 86.0, bottom 7.2)
- Frontier models have cleared the easy/medium tier (root cause correct in 59/60 episodes) but the hard tier — compound and intermittent faults — carries the separation: 15/40 at the frontier
- The open 3B–8B tier, the weight class that runs on-device or on-robot, averages 17.0/100
- A leak and integrity audit of all 225 transcripts is published with the results: zero ground-truth leakage in agent-visible text, deterministic environment, uncoached single-revision prompt

**Design details**
- Deterministic, physics-grounded world model with documented fault physics
- 16 failure modes across battery, grounds, ignition, and ECU/CAN subsystems, including red-herring and compound-fault scenarios
- Cost model benchmarked against expert-technician baseline times

**Selected Artifacts**
- [Second Nature](https://snlabs.dev)
- [GitHub Repo](https://github.com/Jaiparmar940/rlenv)
- [Benchmark Results](https://github.com/Jaiparmar940/rlenv/blob/main/results/results.md)
- [Leak & Integrity Audit](https://github.com/Jaiparmar940/rlenv/blob/main/results/audit.md)

`,
    technologies: ['Inspect AI', 'RL Environments', 'Evals', 'Python', 'LLM Agents', 'Simulation', 'Physics Modeling', 'Benchmarking'],
    image: `${process.env.PUBLIC_URL}/static/projects/no-start-env/results.svg`,
    github: 'https://github.com/Jaiparmar940/rlenv',
    live: 'https://snlabs.dev',
    featured: true,
    hasDetailPage: true,
    detailSlug: 'no-start-env',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/no-start-env/results.svg`
    ]
  },
  {
    id: 29,
    title: 'Lucent — Cross-Border Cardiology Second Opinions',
    category: 'Healthcare, Full-Stack Engineering, Applied AI',
    description: 'Built an AI-assisted healthcare platform that organizes complex cardiac records, evaluates case completeness, and connects patients in India with American cardiologists for physician-delivered second opinions.',
    longDescription: `# Lucent — AI-Assisted Cardiology Second-Opinion Platform (Summer 2026)
**Role:** Founder & Lead Full-Stack Engineer

**Overview**
Built a healthcare platform that connects patients in India with American cardiologists for second opinions on major cardiac decisions — bypass surgery versus stenting, abnormal stress tests, reduced ejection fraction, and CT/calcium score findings. Patients upload medical records in whatever form they have them (PDFs, phone photos of printed reports, scanned records, DICOM studies, angiogram videos), and the platform classifies each document, extracts structured clinical data, and scores whether the case contains enough information for a physician to render an opinion. Physicians receive an organized, review-ready case with document annotation, opinion drafting, and video recording tools. 26,000+ lines of TypeScript across 208 files and 18 database tables.

Explicitly not a diagnostic engine: the AI handles intake, organization, and completeness checking. Every medical opinion comes from a licensed physician.

**Problem / Challenge**
The bottleneck in cross-border second opinions is not physician availability — it is incomplete records. A cardiologist cannot advise on bypass versus stenting without coronary anatomy, ventricular function, and ischemia testing, and patients rarely know that. Cases arrive missing critical studies, physicians spend unpaid time chasing records, and turnaround collapses.

The naive fix — one universal checklist — fails immediately, because required data diverges sharply by condition: a valve case needs gradients and valve area, an atrial fibrillation case needs stroke-risk inputs and anticoagulation status, and neither needs what the other does. Compounding this, the source documents are adversarial to parse: multi-hospital formats, no standard schema, scanned pages requiring OCR, and clinical values buried in prose. Records are also PHI belonging to patients who have not yet paid, creating retention and consent obligations before a case even reaches a physician.

**Approach / Solution**
- Built a four-tier document extraction pipeline that escalates only as needed — content-hash cache → local regex/heuristics → text LLM → vision LLM on rasterized pages — with OCR fallback (Tesseract) and PDF rasterization for scanned documents, minimizing cost per document while preserving accuracy on hard inputs
- Designed an LLM classifier covering 11 cardiology document types (angiography, echo, ECG, labs, medication reconciliation, consultation notes, stress tests, ambulatory rhythm monitors, cardiac CT, cardiac MRI, device interrogation) across three independent signals: filename patterns, content keyword scoring, and LLM classification
- Extracted structured clinical entities — medications with doses, lab results, coronary lesions, valve findings, diagnoses, procedures, symptoms, and scalar measurements — each carrying a confidence level and a source citation quoting the originating document, so every fact shown to a physician is traceable back to the page it came from
- Replaced the flat checklist with a modular completeness engine: an always-active core module plus condition modules (coronary disease, heart failure, arrhythmia, atrial fibrillation, valvular disease) that activate from extracted clinical data rather than patient self-description, since the referral documents are a far stronger signal than a patient's paraphrase
- Encoded 46 requirements (24 critical) grounded in ACC/AHA and ESC guidelines, each with a patient-facing explanation of why it matters, whether a document or the patient can supply it, and a targeted follow-up question
- Made module activation confidence-scored: ambiguous cases surface a disambiguation question to the patient instead of silently guessing a pathway, and patient answers feed back into the readiness score
- Built the physician workspace: DICOM/PDF/video viewer with persistent annotations (region boxes, highlights, text spans, Cornerstone.js measurements, timestamped video notes), opinion drafting with cited findings, video opinion recording with auto-generated slide decks, real-time messaging over server-sent events, availability scheduling, and specialty-weighted case matching with capacity limits
- Implemented the compliance layer as a first-class concern: AES-256-GCM encryption at rest for patient identifiers, hashed date-of-birth lookups, versioned consent records capturing terms and privacy versions with hashed IP, an append-only audit event log, TTL-based case expiry with a scheduled cleanup job, and soft deletes so physician opinions outlive the anonymous case data

**Impact / Results**
- Delivered an end-to-end platform: patient intake, document upload to S3, extraction, completeness scoring, Stripe payment, physician matching, review, and opinion delivery
- Reduced the completeness problem from a single generic checklist to condition-specific requirements across six clinical modules, so a valve patient is never asked for coronary data they do not need — and a valve patient's gradient requirement is never silently skipped
- Extraction tiering keeps the expensive vision-LLM path reserved for documents that actually need it; cached and locally-parseable documents never reach a paid model
- Traceable extraction means a physician can verify any extracted value against the source document rather than trusting the model — a prerequisite for clinical adoption
- Shipped the modular completeness refactor with a regression test suite covering pathway activation, false-positive guards, and patient-input handling; the engine now correctly produces zero coronary requirements for a pure valve case, which the previous default-to-CAD behavior got wrong

**Leadership & Collaboration**
- Owned the full product from problem definition through architecture, implementation, and clinical-requirements research across frontend, backend, data model, AI pipeline, and compliance
- Made and documented the core architectural call — route clinical modules from extracted document data rather than patient self-description — after determining that patient descriptions of their own cardiac condition are too unreliable to drive a requirements checklist
- Directed guideline research (ACC/AHA, ESC, procedure appropriate-use criteria) into a structured per-module requirements specification, then implemented it as typed, testable code
- Designed the schema boundary between anonymous, TTL-expiring patient cases and durable physician opinions, so clinical work product survives patient data retention limits
- Structured delivery as reviewable, stacked pull requests with tests and migration notes rather than monolithic commits

**Skills Applied**
TypeScript, Next.js, React, PostgreSQL, Drizzle ORM, AWS S3, Stripe, LLM prompt engineering, structured data extraction, OCR, DICOM imaging, Cornerstone.js, server-sent events, real-time messaging, AES-256-GCM encryption, authentication and session design, healthcare compliance, clinical informatics, cardiology domain modeling, guideline research, test-driven refactoring, product architecture

**Selected Artifacts**
- [GitHub Repo](https://github.com/Jaiparmar940/lucent)

`,
    technologies: ['Next.js 16', 'React 19', 'TypeScript', 'PostgreSQL', 'Drizzle ORM', 'AWS S3', 'Stripe', 'Cornerstone.js', 'DICOM', 'Tesseract OCR', 'pdf.js', 'LLM Extraction', 'Server-Sent Events', 'AES-256-GCM', 'Tailwind CSS', 'Healthcare Compliance', 'Clinical Informatics', 'Full-Stack Engineering'],
    image: `${process.env.PUBLIC_URL}/static/projects/lucent/hero.svg`,
    github: 'https://github.com/Jaiparm940/lucent',
    live: null,
    featured: false,
    hasDetailPage: true,
    detailSlug: 'lucent-cardiology-second-opinions',
    images: [
      `${process.env.PUBLIC_URL}/static/projects/lucent/hero.svg`
    ]
  }
];

export const getProjectBySlug = (slug) => {
  return projectsData.find(project => project.detailSlug === slug);
}; 